import { useState, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Trophy, ChevronDown, ChevronUp, Battery, Zap, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { InfoTooltip } from '@/components/ui/info-tooltip';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { getDemoSoc } from '@/lib/demoData';
import { currentSupabaseEnv } from '@/lib/supabase';
import { liveBatteryView, type LiveBatteryView } from '@/lib/legacyStatusIcons';
import { BatteryCriticalTag } from '@/components/unit/BatteryCriticalTag';
import type { ProductionEnergyRow, UnitEnergyRow } from '@/types/energy';

type SortColumn = 'offGrid' | 'battery' | 'energy' | 'diesel' | 'saved' | 'co2' | null;
type SortDirection = 'asc' | 'desc';

interface ProductionLeaderboardProps {
  data: ProductionEnergyRow[];
  unitData?: UnitEnergyRow[];
}

/**
 * Renders a compact rank badge showing a numeric rank with distinct visuals for the top three positions.
 *
 * @param rank - The numeric rank to display. Ranks 1–3 use specific highlight styles; all other ranks use a muted style.
 * @returns A JSX span element containing the styled rank number.
 */
function RankBadge({ rank }: { rank: number }) {
  const badgeStyles = {
    1: 'bg-yellow-500/20 text-yellow-500 border-yellow-500/30',
    2: 'bg-gray-400/20 text-gray-400 border-gray-400/30',
    3: 'bg-amber-600/20 text-amber-600 border-amber-600/30',
  };

  const style = badgeStyles[rank as keyof typeof badgeStyles] || 'bg-muted text-muted-foreground border-border';

  return (
    <span className={`inline-flex items-center justify-center w-6 h-6 rounded text-xs font-bold border ${style}`}>
      {rank}
    </span>
  );
}

/**
 * What a row's battery cell shows (T-197): the server's reading and App's band from
 * live_status, like the map. null: a unit that has moved on to another company (F04),
 * whose live battery is theirs.
 */
function batteryView(row: UnitEnergyRow): LiveBatteryView | null {
  if (row.batteryLevel == null) return null;
  const own = currentSupabaseEnv === 'demo' && row.batteryLevel === 0 && row.unitId ? getDemoSoc(row.unitId) : row.batteryLevel;
  return liveBatteryView(row.live_status, own);
}

/**
 * Renders a battery level indicator coloured by the server's band
 */
function BatteryIndicator({ view }: { view: LiveBatteryView | null }) {
  if (view == null) return <span className="text-xs text-muted-foreground" title="Not shown: this unit is with another company now">—</span>;
  if (view.level == null) return <span className="text-xs text-muted-foreground" title="Battery: no current reading">—</span>;
  return (
    <div className={`flex items-center gap-1 ${view.text ?? 'text-muted-foreground'}`} title={`Battery: ${view.level}%${view.critical ? ' · Critical' : ''}`}>
      <Battery className="h-3.5 w-3.5" />
      <span className="text-xs font-medium">{view.level}%</span>
      <BatteryCriticalTag show={view.critical} />
    </div>
  );
}

/**
 * Renders a sortable column header with sort indicator
 */
function SortableHeader({
  column,
  currentSort,
  currentDirection,
  onSort,
  children,
  align = 'left',
}: {
  column: SortColumn;
  currentSort: SortColumn;
  currentDirection: SortDirection;
  onSort: (column: SortColumn) => void;
  children: React.ReactNode;
  align?: 'left' | 'center' | 'right';
}) {
  const isActive = currentSort === column;
  const alignClass = align === 'right' ? 'justify-end' : align === 'center' ? 'justify-center' : 'justify-start';

  return (
    <button
      onClick={() => onSort(column)}
      className={`flex items-center gap-1 hover:text-foreground transition-colors w-full ${alignClass} ${
        isActive ? 'text-[#8FB560]' : 'text-muted-foreground'
      }`}
      aria-label={`Sort by ${column}`}
    >
      <span className="whitespace-nowrap">{children}</span>
      {isActive ? (
        currentDirection === 'desc' ? (
          <ArrowDown className="h-3 w-3" />
        ) : (
          <ArrowUp className="h-3 w-3" />
        )
      ) : (
        <ArrowUpDown className="h-3 w-3 opacity-50" />
      )}
    </button>
  );
}

/**
 * Renders a responsive energy leaderboard showing individual units ranked by off-grid share.
 *
 * The list is truncated by default (5 items on mobile, 10 on desktop) and can be expanded to show all entries.
 *
 * @param data - Array of group rows (for backwards compatibility).
 * @param unitData - Array of individual unit rows to display in the leaderboard.
 * @returns The rendered Energy Leaderboard card element.
 */
export function ProductionLeaderboard({ data, unitData }: ProductionLeaderboardProps) {
  const [showAll, setShowAll] = useState(false);
  const [sortColumn, setSortColumn] = useState<SortColumn>('offGrid');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const defaultCount = 10;

  // Prefer unit data if available, otherwise fall back to group data
  const hasUnitData = unitData && unitData.length > 0;

  // Handle sort toggle - click same column toggles direction, new column sorts desc by default
  const handleSort = useCallback((column: SortColumn) => {
    if (column === sortColumn) {
      setSortDirection(prev => prev === 'desc' ? 'asc' : 'desc');
    } else {
      setSortColumn(column);
      setSortDirection('desc');
    }
  }, [sortColumn]);

  // Sort data based on current sort state
  const sortedUnitData = useMemo(() => {
    if (!unitData || unitData.length === 0) return [];
    if (!sortColumn) return unitData;

    // Battery sorts by the number each cell shows (T-197).
    const shown = (r: UnitEnergyRow) => batteryView(r)?.level ?? null;
    const sorted = [...unitData].sort((a, b) => {
      // No battery to rank ("—": moved on, or no current reading): last in either direction.
      if (sortColumn === 'battery' && (shown(a) == null || shown(b) == null)) {
        return (shown(a) == null ? 1 : 0) - (shown(b) == null ? 1 : 0);
      }
      let comparison = 0;
      switch (sortColumn) {
        case 'offGrid':
          comparison = a.offGridShare - b.offGridShare;
          break;
        case 'battery':
          comparison = (shown(a) ?? 0) - (shown(b) ?? 0);
          break;
        case 'energy':
          comparison = (a.energyCreated || 0) - (b.energyCreated || 0);
          break;
        case 'diesel':
          comparison = (a.dieselSaved || 0) - (b.dieselSaved || 0);
          break;
        case 'saved':
          comparison = a.energyCostSaved - b.energyCostSaved;
          break;
        case 'co2':
          comparison = a.co2Reduced - b.co2Reduced;
          break;
      }
      return sortDirection === 'desc' ? -comparison : comparison;
    });
    return sorted;
  }, [unitData, sortColumn, sortDirection]);

  const sortedProductionData = useMemo(() => {
    if (!data || data.length === 0) return [];
    if (!sortColumn) return data;

    const sorted = [...data].sort((a, b) => {
      let comparison = 0;
      switch (sortColumn) {
        case 'offGrid':
          comparison = a.offGridShare - b.offGridShare;
          break;
        case 'saved':
          comparison = a.energyCostSaved - b.energyCostSaved;
          break;
        case 'co2':
          comparison = a.co2Reduced - b.co2Reduced;
          break;
        default:
          comparison = 0;
      }
      return sortDirection === 'desc' ? -comparison : comparison;
    });
    return sorted;
  }, [data, sortColumn, sortDirection]);

  const displayData = hasUnitData
    ? (showAll ? sortedUnitData : sortedUnitData.slice(0, defaultCount))
    : (showAll ? sortedProductionData : sortedProductionData.slice(0, defaultCount));
  const totalCount = hasUnitData ? unitData.length : data.length;

  return (
    <Card className="relative">
      <div className="absolute top-1.5 right-2 z-10">
        <InfoTooltip
          content={
            <div className="space-y-1">
              <p className="font-semibold">Energy Leaderboard</p>
              <p>Rankings of individual units or production groups by off-grid energy performance and environmental impact.</p>
              <p className="text-muted-foreground">Click column headers to sort by different metrics. Shows battery levels, energy generated, diesel saved, costs saved, and CO₂ reduced.</p>
            </div>
          }
        />
      </div>
      <CardHeader className="pb-2">
        <div className="flex items-center gap-1">
          <Trophy className="h-3.5 w-3.5 text-[#8FB560]" />
          <h3 className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">
            Energy Leaderboard
          </h3>
        </div>
      </CardHeader>
      <CardContent className="px-4 pb-3">
        <div className="overflow-x-auto -mx-4 px-4">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="w-[40px] pl-0">#</TableHead>
                <TableHead className="whitespace-nowrap">{hasUnitData ? 'Unit' : 'Production'}</TableHead>
                <TableHead className="w-[85px]">
                  <SortableHeader
                    column="offGrid"
                    currentSort={sortColumn}
                    currentDirection={sortDirection}
                    onSort={handleSort}
                    align="right"
                  >
                    Off-Grid
                  </SortableHeader>
                </TableHead>
                {hasUnitData && (
                  <TableHead className="w-[85px]">
                    <SortableHeader
                      column="battery"
                      currentSort={sortColumn}
                      currentDirection={sortDirection}
                      onSort={handleSort}
                      align="center"
                    >
                      Battery
                    </SortableHeader>
                  </TableHead>
                )}
                {hasUnitData && (
                  <TableHead className="w-[95px]">
                    <SortableHeader
                      column="energy"
                      currentSort={sortColumn}
                      currentDirection={sortDirection}
                      onSort={handleSort}
                      align="right"
                    >
                      Solar
                    </SortableHeader>
                  </TableHead>
                )}
                {hasUnitData && (
                  <TableHead className="w-[75px]">
                    <SortableHeader
                      column="diesel"
                      currentSort={sortColumn}
                      currentDirection={sortDirection}
                      onSort={handleSort}
                      align="right"
                    >
                      Diesel
                    </SortableHeader>
                  </TableHead>
                )}
                <TableHead className="w-[90px]">
                  <SortableHeader
                    column="saved"
                    currentSort={sortColumn}
                    currentDirection={sortDirection}
                    onSort={handleSort}
                    align="right"
                  >
                    Saved
                  </SortableHeader>
                </TableHead>
                <TableHead className="w-[65px] pr-0">
                  <SortableHeader
                    column="co2"
                    currentSort={sortColumn}
                    currentDirection={sortDirection}
                    onSort={handleSort}
                    align="right"
                  >
                    CO₂
                  </SortableHeader>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {totalCount === 0 ? (
                <TableRow>
                  <TableCell colSpan={hasUnitData ? 8 : 5} className="text-center text-muted-foreground py-8">
                    No energy data available
                  </TableCell>
                </TableRow>
              ) : hasUnitData ? (
                // Unit-level leaderboard
                (displayData as UnitEnergyRow[]).map((row, index) => (
                  <TableRow key={row.unitId} className="h-12">
                    <TableCell className="py-1.5 pl-0">
                      <RankBadge rank={index + 1} />
                    </TableCell>
                    <TableCell className="py-1.5">
                      <div className="flex flex-col max-w-[200px]">
                        <div className="flex items-center gap-1.5">
                          <Link
                            to={`/map?unit=${row.unitCode}&view=detail`}
                            className="font-semibold font-mono text-[#8FB560] hover:underline hover:text-[#a3c574] transition-colors text-sm"
                            title={row.unitCode}
                          >
                            {row.unitCode}
                          </Link>
                          {row.currentGeneration > 0 && (
                            <span className="text-[10px] text-yellow-500 flex items-center gap-0.5">
                              <Zap className="h-2.5 w-2.5" />
                              {row.currentGeneration}
                            </span>
                          )}
                        </div>
                        <span className="text-xs text-muted-foreground truncate" title={row.groupName ?? undefined}>
                          {row.groupName ?? '—'}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="py-1.5 text-right">
                      <span className={row.offGridShare >= 80 ? 'text-[#8FB560] font-semibold' : ''}>
                        {row.offGridShare}%
                      </span>
                    </TableCell>
                    <TableCell className="py-1.5">
                      <div className="flex justify-center">
                        <BatteryIndicator view={batteryView(row)} />
                      </div>
                    </TableCell>
                    <TableCell className="py-1.5 text-right text-xs tabular-nums whitespace-nowrap">
                      {(row.energyCreated || 0).toLocaleString()} <span className="text-muted-foreground">kWh</span>
                    </TableCell>
                    <TableCell className="py-1.5 text-right text-xs tabular-nums whitespace-nowrap">
                      {(row.dieselSaved || 0).toFixed(1)}<span className="text-muted-foreground">g</span>
                    </TableCell>
                    <TableCell className="py-1.5 text-right text-sm whitespace-nowrap">
                      ${row.energyCostSaved.toLocaleString()}
                    </TableCell>
                    <TableCell className="py-1.5 text-right text-sm pr-0 whitespace-nowrap">
                      {row.co2Reduced}<span className="text-muted-foreground">t</span>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                // Production-level leaderboard (fallback)
                (displayData as ProductionEnergyRow[]).map((row, index) => (
                  <TableRow key={row.groupName} className="h-12">
                    <TableCell className="py-1.5 pl-0">
                      <RankBadge rank={index + 1} />
                    </TableCell>
                    <TableCell className="py-1.5">
                      <div className="flex flex-col max-w-[160px]">
                        <div className="flex items-center gap-1.5">
                          <span className="font-semibold truncate" title={row.siteName}>
                            {row.siteName}
                          </span>
                          <span className="text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded shrink-0">
                            {row.unitCount} {row.unitCount === 1 ? 'unit' : 'units'}
                          </span>
                        </div>
                        <span className="text-xs text-muted-foreground truncate" title={row.groupName}>
                          {row.groupName}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="py-1.5 text-right">
                      <span className={row.offGridShare >= 80 ? 'text-[#8FB560] font-semibold' : ''}>
                        {row.offGridShare}%
                      </span>
                    </TableCell>
                    <TableCell className="py-1.5 text-right text-sm whitespace-nowrap">
                      ${row.energyCostSaved.toLocaleString()}
                    </TableCell>
                    <TableCell className="py-1.5 text-right text-sm pr-0 whitespace-nowrap">
                      {row.co2Reduced}<span className="text-muted-foreground">t</span>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {totalCount > defaultCount && (
          <Button
            variant="ghost"
            size="sm"
            className="w-full mt-2 text-muted-foreground hover:text-foreground"
            onClick={() => setShowAll(!showAll)}
          >
            {showAll ? (
              <>
                <ChevronUp className="h-4 w-4 mr-1" />
                Show Less
              </>
            ) : (
              <>
                <ChevronDown className="h-4 w-4 mr-1" />
                Show All {totalCount} {hasUnitData ? 'Units' : 'Productions'}
              </>
            )}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}